/**
 * Formatting helpers for the admin area. Everything is rendered in Darwin
 * time (+09:30, no daylight saving) like the real backend, whatever the
 * browser's own timezone is.
 */
import { now } from '../../clock';

const DARWIN_MS = 9.5 * 3600 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A Date whose UTC getters read Darwin wall-clock time. */
export function darwin(d: Date): Date {
    return new Date(d.getTime() + DARWIN_MS);
}

/** Monolog-style timestamp with milliseconds: 2026-10-19T10:41:22.118+09:30 */
export function isoMs(d: Date): string {
    return darwin(d).toISOString().replace(/Z$/, '+09:30');
}

/** Jira's ISO flavour: 2026-10-14T10:22:31+0930 */
export function jiraIso(d: Date): string {
    return darwin(d).toISOString().replace(/\.\d{3}Z$/, '+0930');
}

/** "14 Oct 2026, 10:22 am" */
export function friendly(d: Date): string {
    const x = darwin(d);
    const h = x.getUTCHours();
    const h12 = h % 12 === 0 ? 12 : h % 12;
    const mm = String(x.getUTCMinutes()).padStart(2, '0');
    return `${x.getUTCDate()} ${MONTHS[x.getUTCMonth()]} ${x.getUTCFullYear()}, ${h12}:${mm} ${h < 12 ? 'am' : 'pm'}`;
}

/** Start of the current Darwin hour, as a real Date. */
export function startOfDarwinHour(d: Date = now()): Date {
    const x = darwin(d);
    x.setUTCMinutes(0, 0, 0);
    return new Date(x.getTime() - DARWIN_MS);
}

/** Stable key for a Darwin hour bucket: 2026-10-19T10 */
export function darwinHourKey(d: Date): string {
    return darwin(d).toISOString().slice(0, 13);
}

/** Darwin hour-of-day (0..23) and weekday (0 = Sunday) for a Date. */
export function darwinHour(d: Date): number {
    return darwin(d).getUTCHours();
}

export function darwinWeekday(d: Date): number {
    return darwin(d).getUTCDay();
}

/** A moment `minutes` before now, rounded down to the minute. */
export function minutesAgo(minutes: number): Date {
    const t = now().getTime() - minutes * 60000;
    return new Date(t - (t % 60000));
}

/** A moment `days` before now at a given Darwin wall-clock time (clamped to the past). */
export function daysAgoAt(days: number, hour: number, minute = 0): Date {
    const x = darwin(now());
    x.setUTCDate(x.getUTCDate() - days);
    x.setUTCHours(hour, minute, 0, 0);
    const d = new Date(x.getTime() - DARWIN_MS);
    return d > now() ? minutesAgo(15 + minute) : d;
}
